import React, { useRef } from 'react';
import { Link } from '@remix-run/react';
import { Button } from '@rocco/components/button';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { useTracker } from '../../../../../tracker';
import { useImpression } from '../../../../../tracker/src/react-bindings';
import { Image } from '@rocco/components/image';

interface AnalyticsAmplifierTileProps {
    image: string; // Main background image
    logo: string; // Logo image
    name: string;
    subtitle: string;
    website: string;
    professionalId: number;
    backgroundColor: string;
    id: number;
}

export const AnalyticsAmplifierTile: React.FC<AnalyticsAmplifierTileProps> = ({
    image,
    logo,
    name,
    subtitle,
    website,
    professionalId,
    backgroundColor,
    id,
}) => {
    const ref = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();

    // Track impression when the card is visible
    useImpression({
        onImpression: async (url: URL) => {
            await tracker.log('AnalyticsAmplifierImpression', {
                url: url,
                targetTracker: ['archiproTracker', 'ga4Tracker'],
                data: {
                    professionalID: professionalId,
                    itemID: id,
                    type: 'AnalyticsAmplifier',
                },
            });
        },
        ref,
    });

    const handleClick = () => {
        // Track click event
        void tracker.log('AnalyticsAmplifierClick', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
            data: {
                ExtraData: JSON.stringify({
                    professionalId: professionalId,
                    itemId: id,
                }),
            },
            ga4Data: {
                professionalId: professionalId,
                itemId: id,
            },
        });
    };
    return (
        <Link
            to={website}
            target="_blank"
            className={`group relative block h-full w-full overflow-hidden rounded-tile shadow-lg`}
            onClick={handleClick}
            ref={ref}
        >
            {/* Background Image */}
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${image})` }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent md:from-black/70 md:via-black/30 md:to-transparent" />
            {/* Logo (top-left) */}
            <div className="absolute left-2.5 top-2 flex items-center gap-2 md:left-4 md:top-4">
                <div
                    className="flex size-13 items-center justify-center overflow-hidden rounded-full p-0.5 md:size-22"
                    style={{
                        backgroundColor: backgroundColor || 'white',
                    }}
                >
                    <Image
                        src={logo}
                        alt={`${name} logo`}
                        size="professionalLogo"
                    />
                </div>
                <span className="w-[7.14519rem] text-wrap text-[0.875rem] font-[500] text-white md:hidden">
                    {name}
                </span>
            </div>
            {/* Content (bottom) */}
            <div className="absolute bottom-0 left-0 right-0 z-10 flex flex-col gap-2 p-4">
                <span className="hidden truncate text-lg font-semibold text-white md:block">
                    {name}
                </span>
                <span className="text-sm text-white/80">{subtitle}</span>
                <div className="hidden justify-end md:flex">
                    <Button
                        size="square42"
                        color="primary"
                        aria-label="Read article"
                    >
                        <ArrowRightIcon />
                    </Button>
                </div>
            </div>
        </Link>
    );
};
